/**
 * Reads a non-httpOnly cookie value from `document.cookie`.
 *
 * Returns the decoded value when the cookie is present, or `null` when it is
 * absent. The backend sets two cookies non-httpOnly by design and both are read
 * through here: `connect_csrf_token`, which the auth interceptor echoes back as
 * the `X-CSRF-Token` header on state-changing requests (double-submit CSRF
 * pattern), and the session hint below (see {@link hasSessionHint}).
 */
export function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Readable, non-httpOnly session-hint cookie the backend sets on login and
 * clears on logout (see `AuthCookieFactory`). It carries no PII or token
 * material — just presence — so the SPA can answer "do I have a session?"
 * synchronously without reading the JWT, which is httpOnly and unreadable by JS.
 * Its lifetime mirrors the refresh cookie exactly: a session cookie (dies on a
 * full browser close) for an untrusted login, or the 90-day Keep-Me-Signed-In
 * window for a trusted one.
 *
 * Exported for specs that need to set or expire it; production code should go
 * through {@link hasSessionHint} rather than re-deriving the predicate.
 */
export const AUTH_HINT_COOKIE = 'connect_auth';

/**
 * Whether the SPA believes it currently has a session.
 *
 * This is a HINT only — presence does not prove the access token is still valid
 * (that is enforced server-side; an expired token yields a 401, which the auth
 * interceptor handles via refresh/logout). Route guards confirm the session for
 * real via `AuthenticationService.me()` (GET /user/me).
 *
 * The single predicate every reader shares. It deliberately tests TRUTHINESS,
 * not `!= null`: a present-but-empty `connect_auth=` counts as NO session. The
 * backend only ever writes `=1`, so this cannot happen today, but the two
 * semantics used to disagree across the three call sites — and truthiness is the
 * fail-closed reading at all of them. The bootstrap clear would wipe a cached
 * profile that has no live session behind it, and `isLoggedIn()` returns false so
 * `AuthGuard` sends the user to login instead of proceeding on a phantom session.
 *
 * NOTE: `index.html`'s inline pre-paint theme script tests for the cookie's
 * presence with its own regex and so still uses presence semantics. It is pinned
 * by a CSP hash and cannot import from here; it only gates an anti-flash paint,
 * so the divergence is cosmetic.
 */
export function hasSessionHint(): boolean {
  return !!readCookie(AUTH_HINT_COOKIE);
}
